import { Trans } from '@lingui/react/macro';
import { useAtom } from 'jotai';

import type { TClaimMessage } from '../../../../@types/rcm';
import {
  FormCheckbox,
  FormRadioGroupImageSets,
  TEETH_IMAGE_SETS_VALUES,
} from '../form';
import { imageSetsAtom, includeFindingTypeAtom } from '../state';

export const ImageInfo = ({
  radiographsTakenDate,
  messages,
  onSelectBitewingOnly,
}: {
  radiographsTakenDate: string;
  messages: TClaimMessage[];
  onSelectBitewingOnly: (value: boolean) => void;
}) => {
  // state
  const [imageSetSelection, setImageSetSelection] = useAtom(imageSetsAtom);
  const [includeFindingTypes, setIncludeFindingTypes] = useAtom(
    includeFindingTypeAtom,
  );

  return (
    <>
      <div className="vai-text-lg vai-font-medium vai-mt-4">
        <Trans>Select finding types to include in summary</Trans>
      </div>
      <div className="vai-grid vai-grid-cols-2 vai-gap-5 vai-mt-4">
        <FormCheckbox
          onChange={(value) =>
            setIncludeFindingTypes('includeCariousLesions', value)
          }
          value={includeFindingTypes.includeCariousLesions}
          label="Include Carious Lesion AI findings"
        />

        <FormCheckbox
          onChange={(value) =>
            setIncludeFindingTypes('includeRadiographicBoneLoss', value)
          }
          value={includeFindingTypes.includeRadiographicBoneLoss}
          label="Include Radiographic Bone-level & Calculus AI findings"
        />

        <FormCheckbox
          onChange={(value) => setIncludeFindingTypes('includeDmf', value)}
          value={includeFindingTypes.includeDmf}
          label="Include DMF AI findings"
        />

        <FormCheckbox
          onChange={(value) =>
            setIncludeFindingTypes('includePeriapicalRadiolucency', value)
          }
          value={includeFindingTypes.includePeriapicalRadiolucency}
          label="Include Periapical Radiolucency AI findings"
        />
      </div>

      <div className="vai-text-lg vai-font-medium vai-mt-4">
        <Trans>Select image sets to include in summary</Trans>
      </div>
      <div className="vai-grid vai-grid-cols-1 vai-gap-5 vai-mt-4">
        <FormRadioGroupImageSets
          value={imageSetSelection}
          onChange={(value) => {
            setImageSetSelection(value);
            if (value === TEETH_IMAGE_SETS_VALUES.All) {
              setImageSetSelection(TEETH_IMAGE_SETS_VALUES.All);
            } else if (value === TEETH_IMAGE_SETS_VALUES.Selected) {
              setImageSetSelection(TEETH_IMAGE_SETS_VALUES.Selected);
            }
          }}
        />
      </div>

      <div className="vai-grid vai-grid-cols-2 vai-gap-5 vai-mt-4">
        <FormCheckbox
          onChange={(value) =>
            setIncludeFindingTypes('includeRadiographsTakenDate', value)
          }
          value={includeFindingTypes.includeRadiographsTakenDate}
          label={`Include date radiographs were taken (${radiographsTakenDate})`}
          id="include-radiographs-taken-date"
        />

        <FormCheckbox
          onChange={(value) => {
            onSelectBitewingOnly(value);
          }}
          value={includeFindingTypes.includeBitewingsOnly}
          label="Include bitewing type only"
        />
      </div>

      {Boolean(messages.length) && (
        <>
          <div className="vai-text-lg vai-font-medium vai-mt-4">
            <Trans>Claim Notes</Trans>
          </div>
          <div className="vai-grid vai-grid-cols-1 vai-gap-5 vai-mt-4">
            {messages.map((msg, i) => (
              <div key={msg.treatmentId ?? i}>
                <div>{msg.text}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};
